import React from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone } from 'react-icons/fi';
import { useBranding } from '../../contexts/BrandingContext';

// Branding-overridable URLs may be external (e.g. notion or a CMS page).
// Use a real anchor for those so the browser navigates off-app; otherwise
// route through react-router for instant SPA navigation.
const SmartLink = ({ href, children, style }) => {
  if (typeof href === 'string' && href.startsWith('/')) {
    return <Link to={href} style={style}>{children}</Link>;
  }
  return <a href={href} target="_blank" rel="noreferrer" style={style}>{children}</a>;
};

/**
 * Authenticated dashboard footer.
 *
 * Two stacked rows:
 *
 *   ┌─ row 1 ─────────────────────────────────────────────────────────┐
 *   │ brand • product   |  Company  Legal  Support  |  email / phone  │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌─ row 2 ──────────── thin divider ───────────────────────────────┐
 *   │ © 2026 Brand • address • GSTIN • v2.0.0                         │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Compact (~110px tall) so it doesn't push the dashboard around. All text
 * sources flow through ``branding`` so the operator can rebrand without a
 * code change.
 */
const WMSFooter = () => {
  const branding = useBranding();

  const linkStyle = {
    color: 'var(--wms-footer-text, #6b7280)',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '0.82rem',
    transition: 'color .15s',
  };
  const colTitle = {
    fontWeight: 700,
    color: 'var(--wms-footer-text, #4b5563)',
    fontSize: '0.7rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: 6,
  };
  const colWrap = {
    display: 'flex', flexDirection: 'column', gap: 4,
  };
  const contactRow = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 999,
    background: 'rgba(0,0,0,0.035)',
    color: 'var(--wms-footer-text, #4b5563)',
    fontSize: '0.78rem', fontWeight: 500,
    textDecoration: 'none',
    transition: 'background .15s',
  };

  const termsHref = branding.footer_terms_url || '/terms';
  const privacyHref = branding.footer_privacy_url || '/privacy';
  const refundHref = branding.footer_refund_url || '/refund-policy';
  const grievanceHref = branding.footer_grievance_url || '/grievance';

  return (
    <footer className="iq-footer ws-footer-v2">
      <div className="container-fluid" style={{ padding: '14px 24px 12px' }}>
        {/* ── Row 1 — brand + link columns + contact chips ────────── */}
        <div className="d-flex flex-column flex-lg-row align-items-lg-start"
             style={{ gap: 18, justifyContent: 'space-between' }}>
          {/* Brand mark */}
          <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 220 }}>
            {/* Footer brand mark — fixed to the Khulja logo (drop the
               asset at public/khulja-logo.png). Falls through to the
               operator's uploaded logo, then to the bundled
               /logo-icon.png, so the footer never renders empty even
               on cold deploys before the asset is in place. */}
            <img
              src="/khulja-logo.png"
              alt="Khulja"
              style={{ height: 30, objectFit: 'contain' }}
              onError={(e) => {
                if (e.currentTarget.dataset.fallback === 'final') return;
                if (branding.logo_url && e.currentTarget.dataset.fallback !== 'branding') {
                  e.currentTarget.dataset.fallback = 'branding';
                  e.currentTarget.src = branding.logo_url;
                } else {
                  e.currentTarget.dataset.fallback = 'final';
                  e.currentTarget.src = '/logo-icon.png';
                }
              }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--wms-footer-text, #1f2937)' }}>
                {branding.platform_name || 'WareShare'}
              </div>
              {branding.platform_tagline && (
                <div style={{ fontSize: '0.74rem', color: 'var(--wms-footer-text, #6b7280)', opacity: 0.85 }}>
                  {branding.platform_tagline}
                </div>
              )}
            </div>
          </div>

          {/* Link columns */}
          <div className="d-flex flex-wrap" style={{ gap: 36, flex: 1, justifyContent: 'center' }}>
            <div style={colWrap}>
              <div style={colTitle}>Company</div>
              <Link to="/about" style={linkStyle}>About</Link>
              <Link to="/faq" style={linkStyle}>FAQ</Link>
              <Link to="/contact" style={linkStyle}>Contact</Link>
            </div>
            <div style={colWrap}>
              <div style={colTitle}>Legal</div>
              <SmartLink href={termsHref} style={linkStyle}>Terms</SmartLink>
              <SmartLink href={privacyHref} style={linkStyle}>Privacy</SmartLink>
              <SmartLink href={refundHref} style={linkStyle}>Refunds</SmartLink>
              <SmartLink href={grievanceHref} style={linkStyle}>Grievance</SmartLink>
              <Link to="/account-deletion" style={linkStyle}>Delete account</Link>
            </div>
            <div style={colWrap}>
              <div style={colTitle}>Resources</div>
              <Link to="/help-center" style={linkStyle}>Help Center</Link>
              <Link to="/notifications/preferences" style={linkStyle}>Notifications</Link>
              <Link to="/settings/api-docs" style={linkStyle}>API Docs</Link>
            </div>
          </div>

          {/* Contact chips */}
          <div className="d-flex flex-wrap" style={{ gap: 8, alignItems: 'flex-start' }}>
            {branding.support_email && (
              <a href={`mailto:${branding.support_email}`} style={contactRow}>
                <FiMail size={13} /> {branding.support_email}
              </a>
            )}
            {branding.support_phone && (
              <a href={`tel:${branding.support_phone}`} style={contactRow}>
                <FiPhone size={13} /> {branding.support_phone}
              </a>
            )}
          </div>
        </div>

        {/* ── Divider ─────────────────────────────────────────────── */}
        <div style={{
          height: 1,
          background: 'rgba(0,0,0,0.06)',
          margin: '12px 0 8px',
        }} />

        {/* ── Row 2 — copyright + legal meta ──────────────────────── */}
        <div className="d-flex flex-column flex-md-row align-items-md-center"
             style={{
               gap: 6,
               fontSize: '0.74rem',
               color: 'var(--wms-footer-text, #6b7280)',
               opacity: 0.9,
               justifyContent: 'space-between',
             }}>
          <div>
            {branding.footer_copyright || `© ${new Date().getFullYear()} ${branding.platform_name || 'WareShare'}. All rights reserved.`}
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 14 }}>
            {branding.company_address && <span>{branding.company_address}</span>}
            {branding.company_gstin && <span>GSTIN: {branding.company_gstin}</span>}
            {branding.app_version && <span>v{branding.app_version}</span>}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default WMSFooter;
